import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useAppSelector } from "features/store";
import type { UserPlatformProfile } from "types/interfaces/user";
import { withShell } from "utils/clientViewRoutes";

export const CLIENT_VIEW_READ_ONLY_TOOLTIP =
  "Client View is read-only. Exit Client View to make changes.";

export interface ViewAs {
  /** True while previewing another user. */
  isClientView: boolean;
  /** Client View is always read-only; mutations are refused in features/api. */
  readOnly: boolean;
  viewedUserId: number | null;
  displayName: string;
  /** The previewed user in Client View, the logged-in user otherwise. */
  profile: UserPlatformProfile | undefined;
  /** The logged-in admin's own profile, for "what may I actually do". */
  realProfile: UserPlatformProfile | undefined;
  /** "" in normal mode, "/client-view/:id" in Client View. Derived from the URL. */
  basePath: string;
  /** Keeps an app path inside the current shell (see utils/clientViewRoutes). */
  href: (path: string) => string;
}

export function useViewAs(): ViewAs {
  const clientView = useAppSelector((state) => state.clientView);
  const realProfile = useAppSelector(
    (state) => state.auth.user?.platform_profile,
  );
  const { pathname } = useLocation();

  // The URL owns the shell, not the slice: clientView stays active after a
  // browser Back out of the shell, and prefixing paths from that stale flag
  // would drag a normal-mode page into client view.
  const shellUserId = useMemo(() => {
    const match =
      matchPath("/client-view/:userId/*", pathname) ??
      matchPath("/client-view/:userId", pathname);
    return match?.params.userId ?? null;
  }, [pathname]);

  return useMemo(() => {
    const isClientView = clientView.active && clientView.userId != null;
    const basePath = shellUserId ? `/client-view/${shellUserId}` : "";
    return {
      isClientView,
      readOnly: isClientView,
      viewedUserId: clientView.userId,
      displayName: clientView.displayName,
      profile:
        (isClientView ? clientView.profile : realProfile) ?? undefined,
      realProfile: realProfile ?? undefined,
      basePath,
      href: (path: string) => withShell(basePath, path) || "/",
    };
  }, [
    clientView.active,
    clientView.userId,
    clientView.displayName,
    clientView.profile,
    realProfile,
    shellUserId,
  ]);
}

export default useViewAs;
